import {
  ACTION, RequestCaseEntityId, ReceiveCaseEntityId,
  FailRequestCaseEntityId, ReceiveConnectedCasePermissions,
  RequestConnectedCasePermissions, RequestChangeConnectionSequence,
  ReceiveChangeConnectionSequence, FailChangeConnectionSequence,
  ReceiveCase, FailLoadCase
} from './action-types';
import { ActionMap } from 'app/area/case/types';
import { CaseState } from 'app/area/case/types-store';
import { getDraftId } from 'app/infrastructure/store';
import { caseDataToStore, caseMetadataToStore } from 'app/infrastructure/store/normalize';
import { SchemaBuilder } from 'app/infrastructure/ui/builder';
import { CasePermissions } from 'app/types-shared';

const reduceFromExisting = (state: CaseState, caseId: number, entityId: number, fetching: boolean) => {
  return {
    ...state,
    stateForCaseIds: {
      ...(state.stateForCaseIds || {}),
      [caseId]: {
        ...(state.stateForCaseIds || {})[caseId],
        valid: !!entityId,
        fetching
      }
    }
  };
};

export const reducers: ActionMap = {

  [ACTION.REQUEST_CASE_ENTITY_ID]: (state: CaseState, action: RequestCaseEntityId): CaseState => {
    return {
      ...reduceFromExisting(state, action.caseId, undefined, true)
    };
  },

  [ACTION.RECEIVE_CASE_ENTITY_ID]: (state: CaseState, action: ReceiveCaseEntityId): CaseState => {
    return {
      ...reduceFromExisting(state, action.caseId, action.entityId, false)
    };
  },

  [ACTION.FAIL_REQUEST_CASE_ENTITY_ID]: (state: CaseState, action: FailRequestCaseEntityId): CaseState => {
    return {
      ...reduceFromExisting(state, action.caseId, undefined, false)
    };
  },

  [ACTION.REQUEST_CONNECTED_CASE_PERMISSIONS]: (state: CaseState, action: RequestConnectedCasePermissions): CaseState => {
    return {
      ...state,
      stateForCaseIds: {
        ...state.stateForCaseIds,
        ...action.ids.reduce((aggr, id) => ({
          ...aggr,
          [id]: {
            ...(state.stateForCaseIds || {})[id],
            fetchingPermissions: true,
            permissions: undefined
          }
        }), {})
      }
    };
  },

  [ACTION.RECEIVE_CONNECTED_CASE_PERMISSIONS]: (state: CaseState, action: ReceiveConnectedCasePermissions): CaseState => {
    return {
      ...state,
      stateForCaseIds: {
        ...state.stateForCaseIds,
        ...Object.keys(action.permissions).reduce((aggr, id) => ({
          ...aggr,
          [id]: {
            ...(state.stateForCaseIds || {})[id],
            fetchingPermissions: false,
            permissions: action.permissions[id]
          }
        }), {} as { [caseId: string]: { valid: boolean; fetching: boolean; permissions: CasePermissions } })
      }
    };
  },

  [ACTION.REQUEST_CHANGE_CONNECTION_SEQUENCE]: (state: CaseState, action: RequestChangeConnectionSequence): CaseState => {
    return {
      ...state,
      stateForCaseIds: {
        ...state.stateForCaseIds,
        [action.connectedCaseId]: {
          ...(state.stateForCaseIds || {})[action.connectedCaseId],
          changeConnectionSequence: {
            caseId: action.connectedCaseId,
            fetching: true
          }
        }
      }
    };
  },

  [ACTION.RECEIVE_CHANGE_CONNECTION_SEQUENCE]: (state: CaseState, action: ReceiveChangeConnectionSequence): CaseState => {
    return {
      ...state,
      stateForCaseIds: {
        ...state.stateForCaseIds,
        [action.connectedCaseId]: {
          ...(state.stateForCaseIds || {})[action.connectedCaseId],
          changeConnectionSequence: {
            caseId: action.connectedCaseId,
            fetching: false
          }
        }
      }
    };
  },

  [ACTION.FAIL_CHANGE_CONNECTION_SEQUENCE]: (state: CaseState, action: FailChangeConnectionSequence): CaseState => {
    return {
      ...state,
      stateForCaseIds: {
        ...state.stateForCaseIds,
        [action.connectedCaseId]: {
          ...(state.stateForCaseIds || {})[action.connectedCaseId],
          changeConnectionSequence: {
            caseId: action.connectedCaseId,
            fetching: false
          }
        }
      }
    };
  },

  [ACTION.RECEIVE_CASE]: (state: CaseState, action: ReceiveCase): CaseState => {
    const docValues: any = caseDataToStore(action.data);
    const metadata = caseMetadataToStore(action.data);
    const pk = getDraftId(docValues);

    return {
      ...state,
      ...{
        selectedId: pk,
        entities: {
          [pk]: docValues
        },
        metadata: {
          [pk]: metadata
        },
        schema: SchemaBuilder.create(state.renderMode, action.uiDefinition),
        uiDefinition: action.uiDefinition,
        fetching: false
      }
    } as CaseState;
  },

  [ACTION.FAIL_CASE_DATA]: (
    state: CaseState,
    action: FailLoadCase
  ): CaseState => {
    return {
      ...state,
      ...{
        selectedId: null,
        fetching: false,
        schema: null
      }
    };
  }
};
